import { useNavigate } from 'react-router';
import { Container, Box, Typography, Alert, Paper } from '@mui/material';
import { Gavel as GavelIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useOidc } from '@nohara-dxdev/react-oidc';

import { WitnessInfoSection } from '../../components/witness-company/witness-info-section';
import { useWitnessCompanyDetailForm } from '../../hooks/witness-company/use-witness-company-detail-form';
import { companiesService } from '../../services/companies.service';

import DetailForm from './detail-form';

import { useBeforeUnloadWarning } from '@/hooks/use-beforeunload-warning';
import { useSubmitting } from '@/hooks/use-submitting';
import { paths } from '@/config/paths';
import { MESSAGES } from '@/lib/constants';
import { useSnackbar } from '@/hooks/use-snackbar';
import { checkPermission } from '@/utils/helpers';
import { FunctionName, PermissionAction } from '@/lib/enums';

export const WitnessCompanyDetailPage = () => {
  const navigate = useNavigate();

  // Use the custom hook to handle all form logic
  const {
    // Data
    witnessCompanyId,
    formData,
    originalFormData,
    // State
    isLoading,
    error,

    // Form actions
    updateField,
    addRepresentative,
    removeRepresentative,
    updateRepresentative,
    handleSubmit,
    // Utility functions
    getFieldError,
    isFieldValid,
    getSalesPersonOptions,
    handleDepartmentChange,

    // Computed properties
    canAddRepresentative,
    isSubmitDisabled,
    hasFormChanges,

    // External data
    departments,
    departmentsLoading,
    staffsLoading,
  } = useWitnessCompanyDetailForm();
  const { setIsSubmitting } = useSubmitting();
  // Show popup warning when user tries to close/refresh page with unsaved changes
  useBeforeUnloadWarning(hasFormChanges);
  const { setSnackbarMessage } = useSnackbar();
  const handleEdit = async () => {
    const success = await handleSubmit();
    if (success) {
      setIsSubmitting(true);
      navigate(paths.companies.path);
      setSnackbarMessage(MESSAGES.WITNESS_COMPANY_HAS_BEEN_CHANGED);
    }
  };

  const handleCancel = () => {
    navigate(paths.companies.path);
  };

  const handleDeleteWitness = async (): Promise<void> => {
    if (!witnessCompanyId) return;
    await companiesService.deleteWitnessCompany(witnessCompanyId);
    setSnackbarMessage(MESSAGES.DELETE_WITNESS_COMPANY);
    setIsSubmitting(true);
    navigate(paths.companies.path);
  };

  const { permissions } = useOidc();
  const [canUpdateWitnessCompany, setCanUpdateWitnessCompany] = useState<boolean>(false);
  const [canDeleteWitnessCompany, setCanDeleteWitnessCompany] = useState<boolean>(false);

  // Check permission on mount - redirect to 404 if user doesn't have READ or UPDATE permission
  useEffect(() => {
    const canRead = checkPermission(
      permissions,
      FunctionName.COMPANY_MANAGEMENT,
      PermissionAction.READ
    );
    const canUpdate = checkPermission(
      permissions,
      FunctionName.COMPANY_MANAGEMENT,
      PermissionAction.UPDATE
    );

    if (!canRead && !canUpdate) {
      navigate('/404');
      return;
    }
  }, [permissions, navigate]);

  useEffect(() => {
    setCanUpdateWitnessCompany(formData.canUpdate ?? false);
    setCanDeleteWitnessCompany(formData.canUpdate ?? false);
  }, [formData]);

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: '#002A76', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <GavelIcon sx={{ fontSize: 40, mr: 2 }} />
          <Box>
            <Typography
              variant='h3'
              fontWeight='bold'
              sx={{
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              }}
            >
              立会会社詳細
            </Typography>
            <Typography
              variant='h6'
              sx={{ opacity: 0.9, mt: 1, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
            >
              立会会社情報の詳細表示・編集
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* General Error Messages */}
      {error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <DetailForm
        canUpdate={canUpdateWitnessCompany}
        canDelete={canDeleteWitnessCompany}
        formData={formData}
        originalFormData={originalFormData}
        getFieldError={getFieldError}
        isFieldValid={isFieldValid}
        addRepresentative={addRepresentative}
        removeRepresentative={removeRepresentative}
        updateRepresentative={updateRepresentative}
        getSalesPersonOptions={getSalesPersonOptions}
        canAddRepresentative={canAddRepresentative}
        departments={departments}
        departmentsLoading={departmentsLoading}
        staffsLoading={staffsLoading}
        handleDepartmentChange={(departmentId) => {
          void handleDepartmentChange(departmentId);
        }}
        isSubmitting={isLoading}
        onDelete={handleDeleteWitness}
        handleCancel={handleCancel}
        handleEdit={() => {
          void handleEdit();
        }}
        isSubmitDisabled={isSubmitDisabled}
        error={error}
      >
        {/* Witness Company Information Section */}
        <WitnessInfoSection
          formData={formData}
          originalFormData={originalFormData}
          onUpdateField={updateField}
          getFieldError={getFieldError}
          isFieldValid={isFieldValid}
        />
      </DetailForm>
    </Container>
  );
};
